import { createRenderer } from './render/renderer';
import { computeLayout, contains, cellRect, columnAt, debugBoundsOn, forcedViewportSize, forceViewport, mapGraphArea, purgeCardRects, restOptionRects, cacheOptionRects, craftOptionRects, craftBackRect, seedBadgeRect, setDebugBounds, socketCardRects, VIEWPORTS, type Layout } from './render/layout';
import { drawGame, drawTitle, drawRewardScreen, drawComponentReward, drawSocketScreen, drawRestScreen, drawPurgeScreen, drawCraftScreen, drawCacheScreen, drawSocketExpandScreen, drawEndScreen, drawFloorBanner, drawMap, resetGhostTracks, type DisplayState } from './render/view';
import {
  createFx,
  updateFx,
  drawFx,
  shakeOffset,
  emitBurst,
  emitTrail,
  addFloat,
  addHurt,
  addStamp,
  addTracer,
  addShake,
  addFlash,
} from './render/fx';
import {
  startPlayback,
  tickPlayback,
  currentWave,
  flashingCells,
  flashProgress,
  fallProgress,
  playbackDone,
  type Playback,
} from './render/playback';
import {
  currentStep,
  startTurnPlayback,
  stepProgress,
  tickTurnPlayback,
  turnPlaybackDone,
  type TurnPlayback,
} from './render/turnplayback';
import { GEM_COLOR, PALETTE } from './render/palette';
import {
  createAnim,
  drawEnemyChar,
  drawPlayerChar,
  progress as animProgress,
  settle as settleAnim,
  setState as setCharState,
  type CharAnim,
  type EnemyCharState,
  type PlayerCharState,
} from './render/character';
import { tierFor, tierRank } from './core/effects';
import type { WaveReport } from './core/resolve';
import * as sfx from './audio/sfx';
import {
  beginAim,
  beginFreeSwap,
  cancelAim,
  clickCard,
  createController,
  handIndexAt,
  isAiming,
  pointerDown,
  rotateAim,
  updateHover,
  type Controller,
} from './input/controller';
import { canPlay, endTurn, setTarget, type CardInstance, type Combat, type CombatEvent } from './core/combat';
import type { ResolveReport } from './core/resolve';
import { idx, type GemKind, type Piece } from './core/types';
import { applyGravity, clearCells, restRow, rotatePiece } from './core/grid';
import {
  availableNodes,
  beginRun,
  chooseCacheReward,
  chooseRewardOption,
  continueReward,
  craftGem,
  createRun,
  currentFight,
  enterMapNode,
  expandSocket,
  restAction,
  restOptions,
  purgeCard,
  runProgress,
  settleBattle,
  skipCache,
  skipReward,
  socketEnchant,
  skipSocket,
  trackResolveScore,
  type RunState,
} from './game/run';
import { CRAFT_RECIPES } from './game/crafting';
import { nodeHitRect, visibleNodes } from './game/map';
import { RUN_VERSION, encodeSeed, generateSeed, seedFromUrl } from './game/seed';
import { setMatchMin, getMatchMin, getSeedDensity, setSeedDensity } from './core/rules';
import { setBoardSize, resetBoardSize, boardLabel, isDefaultBoard, BOARD_SIZES, WELL_COLS, WELL_ROWS } from './core/types';

const mount = document.getElementById('app');
if (!mount) throw new Error('#app missing');

const renderer = createRenderer(mount);
const { ctx } = renderer;
const controller: Controller = createController();
const reduceMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const fx = createFx(reduceMotion);
const sound = sfx.createSfx();

// --- character presentation seam ------------------------------------------
// Final art does not exist. These clocks establish *when* a character reacts,
// so dropping sprites in later is an art task rather than a rewrite. Nothing
// here touches combat simulation.
const enemyAnims = new Map<number, CharAnim<EnemyCharState>>();
const playerAnim = createAnim<PlayerCharState>('idle');

function enemyAnim(uid: number): CharAnim<EnemyCharState> {
  let a = enemyAnims.get(uid);
  if (!a) {
    a = createAnim<EnemyCharState>('idle');
    enemyAnims.set(uid, a);
  }
  return a;
}

function setEnemyState(uid: number, state: EnemyCharState, dur: number): void {
  setCharState(enemyAnim(uid), state, dur);
}

function setPlayerState(state: PlayerCharState, dur: number): void {
  setCharState(playerAnim, state, dur);
}

/**
 * Advance every character clock. `anticipate` resolves into `attack` on its
 * own so a beat can open with the wind-up and still land the strike without
 * a second call site remembering to fire it.
 */
function advanceAnims(): void {
  for (const a of enemyAnims.values()) {
    if (a.state === 'anticipate' && animProgress(a) >= 1) {
      setCharState(a, 'attack', 380);
      continue;
    }
    settleAnim(a, 'idle');
  }
  settleAnim(playerAnim, 'idle');
}

function resetAnims(): void {
  enemyAnims.clear();
  setCharState(playerAnim, 'idle', 0);
}

/**
 * Run identity. `?seed=` accepts a display code or a plain integer; without
 * it a fresh seed is generated. `?matchMin=` sets the match threshold (3 or
 * 4). `?board=` sets the well size (7x9, 7x10, or 8x10). Everything
 * downstream reads `run.seed`, never a captured generator.
 */
const initialSeed = seedFromUrl(window.location.search) ?? generateSeed();
{
  const params = new URLSearchParams(window.location.search);
  const mm = params.get('matchMin');
  if (mm !== null) {
    const n = Number(mm);
    if (Number.isFinite(n)) setMatchMin(n);
  }
  const board = params.get('board');
  if (board !== null) {
    const clean = board.toLowerCase().replace(/[^0-9x]/g, '');
    const parts = clean.split('x');
    if (parts.length === 2) {
      const c = Number(parts[0]);
      const r = Number(parts[1]);
      if (Number.isFinite(c) && Number.isFinite(r) && c > 0 && r > 0) setBoardSize(c, r);
    }
  }
  const density = params.get('density');
  if (density !== null) {
    const n = Number(density);
    if (Number.isFinite(n)) setSeedDensity(n);
  }
}
const run: RunState = createRun(initialSeed);

/** Display code for the current seed. Regenerated when the seed changes. */
function seedCode(): string {
  return encodeSeed(run.seed);
}

/** Set when the player copies the seed, so the badge can confirm it. */
let seedCopied = false;
let seedCopiedAt = 0;

function copySeed(): void {
  const code = seedCode();
  const done = () => {
    seedCopied = true;
    seedCopiedAt = performance.now();
  };
  try {
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(code).then(done, () => undefined);
      return;
    }
  } catch {
    // Clipboard denied or unavailable; the code is still on screen.
  }
  done();
}

let playback: Playback | null = null;
let emittedWave = -1;
/** Enemy turn, played back one beat at a time. */
let turnPlayback: TurnPlayback | null = null;
let turnStepEmitted = -1;
let turnLanded = false;
let lastLogCount = 0;
/** Seconds of beat after a fight ends, before the reward screen. */
let endTimer = -1;
/** Pointer position in layout pixels, for hover outside the battle. */
let mouseX = -1;
let mouseY = -1;
/** Brief confirmation drawn on the map screen (rest heals, etc.). */
let mapNotice: string | null = null;
let mapNoticeAt = 0;

function activeNotice(): string | null {
  if (mapNotice && performance.now() - mapNoticeAt > 2200) mapNotice = null;
  return mapNotice;
}

/** Available map node under the pointer, or null. */
function mapNodeAt(layout: Layout, x: number, y: number) {
  const area = mapGraphArea(layout);
  for (const node of availableNodes(run)) {
    const r = nodeHitRect(node, area);
    if (contains(r, x, y)) return node;
  }
  return null;
}

/** Keyboard-focused map node index within the visible node list. -1 = none. */
let mapFocusIndex = -1;

/** All visible map node ids in draw order (for keyboard focus cycling). */
function visibleMapNodeIds(): string[] {
  const visible = visibleNodes(run.map, run.currentNodeId, 3);
  for (const id of run.visitedNodes) visible.add(id);
  const boss = run.map.nodes.find(n => n.type === 'boss');
  if (boss) visible.add(boss.id);
  return run.map.nodes.filter(n => visible.has(n.id)).map(n => n.id);
}

/** Visible map node under the pointer (for tooltips), or null. */
function visibleMapNodeAt(layout: Layout, x: number, y: number) {
  const area = mapGraphArea(layout);
  const visible = visibleNodes(run.map, run.currentNodeId, 3);
  for (const id of run.visitedNodes) visible.add(id);
  const boss = run.map.nodes.find(n => n.type === 'boss');
  if (boss) visible.add(boss.id);
  for (const node of run.map.nodes) {
    if (!visible.has(node.id)) continue;
    const r = nodeHitRect(node, area);
    if (contains(r, x, y)) return node;
  }
  return null;
}

/** Enter a map node from click or key, resetting presentation when combat starts. */
function chooseMapNode(nodeId: string): void {
  const before = run.hp;
  enterMapNode(run, nodeId);
  mapFocusIndex = -1;
  if (run.phase === 'map' && run.hp > before) {
    mapNotice = `RESTED  +${run.hp - before} HP`;
    mapNoticeAt = performance.now();
  }
  if (run.phase === 'battle') {
    resetPresentation();
    lastLogCount = 0;
  }
}

function activeCombat(): Combat | null {
  return run.phase === 'battle' ? run.combat : null;
}

/**
 * The size layout should be computed against. Normally the real canvas; a
 * simulated phone size while the debug harness is active, so screen sizes can
 * be exercised without opening DevTools.
 */
function layoutSize(): { width: number; height: number } {
  return forcedViewportSize() ?? { width: renderer.width, height: renderer.height };
}

/**
 * Hand carousel offset. The window shows ~3.5 cards and follows the focused
 * one, so each card can be large and readable instead of five fighting for a
 * phone screen.
 */
let handScroll = 0;

/** Layout for the current battle, sized to the hand and the enemy count. */
function battleLayout(combat: Combat | null) {
  const size = layoutSize();
  return computeLayout(
    size.width,
    size.height,
    combat?.hand.length ?? 0,
    combat?.enemies.length ?? 1,
    handScroll,
  );
}

/**
 * Which hand index the carousel should centre on, or -1 to leave it still.
 *
 * Deliberately excludes hover. Hovering a card raises it in place — the hand
 * must not slide around under the pointer while the player is just looking.
 * The window only moves when a card is held or already selected.
 */
function centeredHandIndex(combat: Combat | null): number {
  if (!combat) return -1;
  if (drag) return drag.index;
  const aimUid = controller.aim.card?.uid;
  if (aimUid === undefined) return -1;
  const i = combat.hand.findIndex(c => c.uid === aimUid);
  return i >= 0 ? i : -1;
}

function busy(): boolean {
  if (playback !== null && !playbackDone(playback)) return true;
  if (turnPlayback !== null && !turnPlaybackDone(turnPlayback)) return true;
  return false;
}

/** Ease-out so a falling piece accelerates like a real drop. */
/**
 * Gravity, plain. Gems and pieces fall — slow at first, accelerating after.
 * The steeper curve tried earlier read as wrong on playtest.
 */
function easeInQuad(k: number): number {
  const t = Math.max(0, k);
  return t * t;
}

/**
 * End the player's turn and start the enemy turn's beat-by-beat playback.
 *
 * Deliberately does NOT start cascade playback: the enemy's own cascades are
 * already captured as `enemyResolve` steps carrying their own grid snapshots,
 * and two competing playbacks would fight over the display.
 */
function endPlayerTurn(combat: Combat): void {
  const steps = endTurn(combat);
  playback = null;
  emittedWave = -1;
  turnPlayback = startTurnPlayback(steps);
  turnStepEmitted = -1;
  // No drainCombatEvents here — that would dump every beat's fx in one frame.
  // emitTurnStep fires them one at a time as the playback advances.
}

function wellCenter(l: Layout): { x: number; y: number } {
  return { x: l.well.x + l.well.w / 2, y: l.well.y + l.well.h / 2 };
}

function heroCenter(l: Layout): { x: number; y: number } {
  return { x: l.heroRail.x + l.heroRail.w / 2, y: l.heroRail.y + l.heroRail.h / 2 };
}

function enemyPanelCenter(l: Layout, idx: number): { x: number; y: number } {
  const panel = l.enemies[idx] ?? l.enemies[0];
  return panel
    ? { x: panel.x + panel.w / 2, y: panel.y + panel.h / 2 }
    : { x: l.well.x + l.well.w / 2, y: l.well.y + l.well.h / 2 };
}

/** Fx and sound for the beat that just became current. */
function emitTurnStep(l: Layout, p: TurnPlayback): void {
  const s = currentStep(p);
  if (!s) return;
  const panel = l.enemies.find((_, i) => i === s.enemyUid - 1) ?? l.enemies[0];
  const ex = panel ? panel.x + panel.w / 2 : l.well.x + l.well.w / 2;
  const ey = panel ? panel.y + 14 : l.well.y;

  switch (s.kind) {
    case 'enemyAttack': {
      // Wind-up, then the strike. The projectile and the pose arrive together.
      setEnemyState(s.enemyUid, 'anticipate', 200);
      // The hit visibly travels from the enemy rail to the hero rail — right
      // to left, reinforcing that both combatants share the same board.
      const hc = heroCenter(l);
      const hudX = l.hud.x + l.hud.w / 2;
      const hudY = l.hud.y + 22;
      addTracer(fx, ex, ey + 30, hc.x, hc.y, PALETTE.danger, 0.24, 3.5);

      if (s.amount > 0) {
        sfx.playPlayerHit(sound);
        setPlayerState('hit', 340);
        addFloat(fx, hudX, hudY, `-${s.amount}`, PALETTE.danger, 22);
        addShake(fx, 4 + Math.min(8, s.amount * 0.6));
        addHurt(fx, 0.18 + Math.min(0.3, s.amount * 0.02));
        // Block absorbed part of it: say so, or the number looks wrong.
        if (s.blocked > 0) {
          addFloat(fx, hudX, hudY + 20, `${s.blocked} BLOCKED`, PALETTE.block, 12);
          sfx.playBlock(sound);
        }
      } else {
        // Fully absorbed. Still a beat, just a quieter one — and no implication
        // of HP loss.
        sfx.playBlock(sound);
        setPlayerState('blocked', 340);
        addFloat(fx, hudX, hudY, 'BLOCKED', PALETTE.block, 15);
      }
      break;
    }
    case 'poisonTick':
      sfx.playEnergy(sound);
      setEnemyState(s.enemyUid, 'hit', 300);
      addFloat(fx, ex, ey + 24, `-${s.amount} POISON`, PALETTE.poison, 15);
      emitBurst(fx, ex, ey + 30, PALETTE.poison, 8, 110);
      break;
    case 'enemyBlock':
      setEnemyState(s.enemyUid, 'block', 420);
      addFloat(fx, ex, ey + 30, `+${s.amount} BLOCK`, PALETTE.block, 14);
      sfx.playBlock(sound);
      // The shell visibly thickens rather than the number silently rising.
      emitBurst(fx, ex, ey + 30, PALETTE.block, 10, 130);
      addShake(fx, 2);
      break;
    case 'pieceDrop': {
      // The beat's whole budget goes to the hold-then-fall; the impact fires
      // from the landing hook in the frame loop, not here. The character
      // crouches for the release.
      setEnemyState(s.enemyUid, 'drop', 750);
      // A brief directional cue: the piece originates from the enemy side.
      const wc = wellCenter(l);
      addTracer(fx, ex, ey + 20, wc.x, wc.y - 20, PALETTE.dim, 0.18, 2);
      break;
    }
    case 'stoneDrop': {
      setEnemyState(s.enemyUid, 'drop', 600);
      const wc = wellCenter(l);
      addTracer(fx, ex, ey + 20, wc.x, wc.y - 20, PALETTE.dim, 0.18, 2);
      break;
    }
    case 'boardPush':
      sfx.playStone(sound);
      setEnemyState(s.enemyUid, 'attack', 500);
      addFlash(fx, 0.2);
      addShake(fx, 8);
      addFloat(fx, ex, ey + 30, `RAISE +${s.amount}`, PALETTE.inert, 15);
      break;
    case 'hexColumn':
      sfx.playCap(sound);
      addFlash(fx, 0.14);
      addShake(fx, 5);
      addFloat(fx, ex, ey + 30, 'HEX', PALETTE.inert, 15);
      break;
    case 'scramble':
      sfx.playEnergy(sound);
      addFloat(fx, ex, ey + 30, `SCRAMBLE ${s.amount}`, PALETTE.volt, 14);
      break;
    case 'enemyResolve': {
      sfx.playMatch(sound, 1);
      addShake(fx, 4);
      const hc = heroCenter(l);
      if (s.amount > 0) {
        addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 22, `-${s.amount} MATCH`, PALETTE.danger, 19);
        // Enemy cascade damage travels right → left toward the player.
        addTracer(fx, ex, ey + 30, hc.x, hc.y, PALETTE.danger, 0.26, 3);
        emitBurst(fx, hc.x, hc.y, PALETTE.danger, 6, 110);
      } else if ((s.report?.totalFrostBlock ?? 0) > 0) {
        addFloat(fx, ex, ey + 30, `+${s.report?.totalFrostBlock ?? 0} BLOCK`, PALETTE.block, 14);
        emitBurst(fx, ex, ey + 30, PALETTE.block, 8, 120);
      } else if ((s.report?.totalPoison ?? 0) > 0) {
        addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 22, `POISON ${s.report?.totalPoison ?? 0}`, PALETTE.poison, 14);
        // Enemy poison travels right → left toward the player.
        addTracer(fx, ex, ey + 30, hc.x, hc.y, PALETTE.poison, 0.26, 2.5);
      } else {
        addFloat(fx, ex, ey + 30, 'THEIR CASCADE', PALETTE.danger, 13);
      }
      break;
    }
    case 'playerPoison':
      sfx.playPlayerHit(sound);
      addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 22, `POISON -${s.amount}`, PALETTE.poison, 19);
      addHurt(fx, 0.2);
      addShake(fx, 3 + Math.min(5, s.amount * 0.4));
      break;
    case 'capDamage': {
      sfx.playCap(sound);
      addFlash(fx, 0.22);
      addHurt(fx, 0.5);
      addShake(fx, 12);
      addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 22, `-${s.amount} CAPPED`, PALETTE.danger, 20);
      // Cap strike originates from the enemy side.
      const hc = heroCenter(l);
      addTracer(fx, ex, ey + 30, hc.x, hc.y, PALETTE.danger, 0.24, 3.5);
      emitBurst(fx, hc.x, hc.y, PALETTE.danger, 8, 120);
      break;
    }
  }
}

/** Reset transient presentation state shared by every run entry point. */
function resetPresentation(): void {
  cancelAim(controller);
  playback = null;
  turnPlayback = null;
  emittedWave = -1;
  turnStepEmitted = -1;
  turnLanded = false;
  lastLogCount = 0;
  endTimer = -1;
  handScroll = 0;
  // Stale ghost tracks from the previous fight would slide the first
  // prediction of the new one in from a board that no longer exists.
  resetGhostTracks();
  resetAnims();
}

/**
 * New run: mint a seed, then take the same reset path as everything else.
 * There is no separate "start" implementation to drift out of sync.
 */
function newRun(freshSeed = true): void {
  if (freshSeed) {
    run.seed = generateSeed();
    seedCopied = false;
  }
  resetPresentation();
  beginRun(run);
}

/**
 * Restart the same seed. `beginRun` re-derives the generator from `run.seed`,
 * so this reproduces the run exactly — it does not continue whatever stream
 * survived the last fight. This is the correctness requirement: a Daily mode
 * that cannot reproduce is not a Daily.
 */
function restartSameSeed(): void {
  resetPresentation();
  beginRun(run);
}

// --- cascade presentation -------------------------------------------------

const TIER_LABEL = {
  charged: 'CHARGED',
  surge: 'SURGE',
  aether: 'AETHER',
} as const;

/** Stamp tinted to the color of the run that earned the tier. */
function tierStampColor(wave: WaveReport): string | null {
  const rank = wave.tier === null ? 0 : tierRank(wave.tier);
  for (const run of wave.runs) {
    if (tierRank(tierFor(run.cells.length)) >= rank) return GEM_COLOR[run.kind];
  }
  return null;
}

function emitWave(l: Layout, p: Playback): void {
  const wave = currentWave(p);
  if (!wave) return;

  const wellCx = l.well.x + l.well.w / 2;
  const wellCy = l.well.y + l.well.h / 2;

  // Match tier and cascade depth are different axes and get different stamps.
  if (wave.tier && wave.tier !== 'base') {
    const label = TIER_LABEL[wave.tier];
    const color = tierStampColor(wave) ?? PALETTE.gold;
    addStamp(fx, wellCx, wellCy - 24, label, color, wave.tier === 'aether' ? 44 : 34);
  }

  // Wave 1 is a match. Everything after it is a cascade, and it should say so.
  if (wave.multiplier > 1) {
    const big = wave.multiplier >= 3;
    addStamp(
      fx,
      wellCx,
      wellCy + 26,
      `CASCADE x${wave.multiplier}`,
      big ? PALETTE.gold : '#ffd9a0',
      big ? 34 : 27,
    );
  }

  // Intensity climbs with depth. Wave 1 is a pop; wave 3 is an event.
  const heat = Math.min(3, wave.wave);
  sfx.playMatch(sound, wave.wave);

  if (wave.tier === 'aether') {
    addShake(fx, 12);
    addFlash(fx, 0.2);
    sfx.playRowClear(sound, 2);
  } else if (wave.tier === 'surge' || wave.tier === 'charged') {
    addShake(fx, 5);
  }
  addShake(fx, heat * 1.6);
  if (heat >= 2) addFlash(fx, 0.04 * (heat - 1));

  // Read the gem kinds off the display grid — during the flash it still holds
  // this wave's cells, which is exactly what the particles should describe.
  for (const cell of wave.cleared) {
    const kind: GemKind | null = p.display[cell] ?? null;
    if (kind === null) continue;
    const r = cellRect(l, cell);
    const cx = r.x + r.w / 2;
    const cy = r.y + r.h / 2;
    emitBurst(fx, cx, cy, GEM_COLOR[kind], (kind === 'stone' ? 3 : 5) + heat * 2, 140 + heat * 30);

    const combat = activeCombat();
    if (kind === 'ember' && wave.emberDamage > 0 && combat) {
      const er = l.enemies[combat.targetIndex] ?? l.enemies[0];
      if (er) emitTrail(fx, cx, cy, er, PALETTE.ember, 2);
    }
    if (kind === 'venom' && wave.poison > 0 && combat) {
      const er = l.enemies[combat.targetIndex] ?? l.enemies[0];
      if (er) emitTrail(fx, cx, cy, er, PALETTE.venom, 2);
    }
    if (kind === 'frost' && wave.frostBlock > 0) {
      emitTrail(fx, cx, cy, l.heroRail, PALETTE.frost, 2);
    }
  }

  if (wave.rowsCleared.length > 0) {
    sfx.playRowClear(sound, wave.rowsCleared.length);
    addFlash(fx, 0.18 + wave.rowsCleared.length * 0.06);
    addShake(fx, 5 + wave.rowsCleared.length * 3);
  } else if (wave.cleared.length >= 3) {
    addShake(fx, 2 + wave.multiplier);
  }

  const combat = activeCombat();
  const dmg = wave.emberDamage + wave.rowBurst;
  if (dmg > 0 && combat) {
    const er = l.enemies[combat.targetIndex] ?? l.enemies[0];
    if (er) addFloat(fx, er.x + er.w / 2, er.y + 10, `-${dmg}`, PALETTE.ember, dmg >= 30 ? 22 : 16);
  }
  if (wave.frostBlock > 0) {
    addFloat(fx, l.hud.x + 40, l.hud.y + 40, `+${wave.frostBlock} BLOCK`, PALETTE.block, 14);
    sfx.playBlock(sound);
  }
  if (wave.energyGained > 0) {
    addFloat(fx, l.hud.x + 40, l.hud.y + 58, `+${wave.energyGained} ENERGY`, PALETTE.volt, 14);
    sfx.playEnergy(sound);
  }

  // Directional wave-level tracers: well → target rail.
  const wC = wellCenter(l);
  if ((wave.emberDamage > 0 || wave.rowBurst > 0) && combat) {
    const er = l.enemies[combat.targetIndex] ?? l.enemies[0];
    if (er) addTracer(fx, wC.x, wC.y, er.x + er.w / 2, er.y + er.h / 2, PALETTE.ember, 0.22, 2.5);
  }
  if (wave.poison > 0 && combat) {
    const er = l.enemies[combat.targetIndex] ?? l.enemies[0];
    if (er) addTracer(fx, wC.x, wC.y, er.x + er.w / 2, er.y + er.h / 2, PALETTE.venom, 0.22, 2);
  }
  if (wave.frostBlock > 0) {
    const hc = heroCenter(l);
    addTracer(fx, wC.x, wC.y, hc.x, hc.y, PALETTE.frost, 0.22, 2);
    emitBurst(fx, hc.x, hc.y, PALETTE.block, 6, 100);
  }
}

function drainCombatEvents(l: Layout): void {
  const combat = activeCombat();
  if (!combat) {
    lastLogCount = 0;
    return;
  }
  const log = combat.log;
  for (let i = lastLogCount; i < log.length; i++) {
    const e = log[i] as CombatEvent | undefined;
    if (!e) continue;
    switch (e.kind) {
      case 'playerDamaged': {
        if (e.amount > 0) {
          sfx.playPlayerHit(sound);
          // The enemy hitting you has to be as loud as you hitting it. Without
          // this the HP bar moves 7% and nothing else happens, which reads as
          // "the enemy did nothing".
          const hc = heroCenter(l);
          const foe = l.enemies[0];
          if (foe) addTracer(fx, foe.x + foe.w / 2, foe.y + foe.h / 2, hc.x, hc.y, PALETTE.danger, 0.24, 3);
          addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 22, `-${e.amount}`, PALETTE.danger, 22);
          emitBurst(fx, hc.x, hc.y, PALETTE.danger, 6, 110);
          addShake(fx, 4 + Math.min(8, e.amount * 0.6));
          addHurt(fx, 0.18 + Math.min(0.3, e.amount * 0.02));
        }
        break;
      }
      case 'enemyDamaged': {
        if (e.amount > 0) {
          sfx.playEnemyHit(sound);
          // Player attack: well → enemy rail (left → right).
          const wc = wellCenter(l);
          const ec = enemyPanelCenter(l, e.enemy - 1);
          addTracer(fx, wc.x, wc.y, ec.x, ec.y, PALETTE.ember, 0.22, 2.5);
          emitBurst(fx, ec.x, ec.y, PALETTE.ember, 6, 110);
        }
        break;
      }
      case 'playerBlocked': {
        if (e.amount > 0) {
          // Frost defense: well → hero rail (toward the player).
          const hc = heroCenter(l);
          const wc = wellCenter(l);
          addTracer(fx, wc.x, wc.y, hc.x, hc.y, PALETTE.block, 0.22, 2.5);
          emitBurst(fx, hc.x, hc.y, PALETTE.block, 6, 100);
          addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 40, `+${e.amount} BLOCK`, PALETTE.block, 13);
        }
        break;
      }
      case 'playerPoisoned': {
        sfx.playEnergy(sound);
        setPlayerState('poisoned', 420);
        // Poison originates from the enemy side — right → left.
        const hc = heroCenter(l);
        const foe = l.enemies[0];
        if (foe) addTracer(fx, foe.x + foe.w / 2, foe.y + foe.h / 2, hc.x, hc.y, PALETTE.poison, 0.24, 2.5);
        addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 22, `POISON ${e.amount}`, PALETTE.poison, 14);
        break;
      }
      case 'enemyPoisoned': {
        // Player venom: well → enemy rail (left → right).
        const wc = wellCenter(l);
        const ec = enemyPanelCenter(l, e.enemy - 1);
        addTracer(fx, wc.x, wc.y, ec.x, ec.y, PALETTE.venom, 0.22, 2);
        emitBurst(fx, ec.x, ec.y, PALETTE.venom, 6, 100);
        break;
      }
      case 'enemyDied':
        // The figure goes down. Losing an enemy should read as a death, not
        // as a panel quietly disappearing.
        setEnemyState(e.enemy, 'defeat', 520);
        break;
      case 'stonesDropped':
      case 'boardPushed':
        sfx.playStone(sound);
        addShake(fx, e.kind === 'boardPushed' ? 6 : 3);
        break;
      case 'gemsDropped':
        sfx.playStone(sound);
        break;
      case 'enemyResolve': {
        // The enemy's own cascade, aimed back at the player.
        sfx.playMatch(sound, 1);
        addShake(fx, 4);
        const hc = heroCenter(l);
        const ec = enemyPanelCenter(l, e.enemy - 1);
        if (e.report.totalDamage > 0) {
          addTracer(fx, ec.x, ec.y, hc.x, hc.y, PALETTE.danger, 0.26, 3);
          emitBurst(fx, hc.x, hc.y, PALETTE.danger, 6, 110);
        }
        if (e.report.totalPoison > 0) {
          addTracer(fx, ec.x, ec.y, hc.x, hc.y, PALETTE.poison, 0.26, 2.5);
        }
        if (e.report.totalFrostBlock > 0) {
          emitBurst(fx, ec.x, ec.y, PALETTE.block, 8, 120);
        }
        break;
      }
      case 'capDamage': {
        sfx.playCap(sound);
        addFlash(fx, 0.22);
        addHurt(fx, 0.5);
        addShake(fx, 12);
        addFloat(fx, l.hud.x + l.hud.w / 2, l.hud.y + 22, `-${e.amount} CAPPED`, PALETTE.danger, 20);
        const hc = heroCenter(l);
        const foe = l.enemies[0];
        if (foe) addTracer(fx, foe.x + foe.w / 2, foe.y + foe.h / 2, hc.x, hc.y, PALETTE.danger, 0.24, 3.5);
        emitBurst(fx, hc.x, hc.y, PALETTE.danger, 8, 120);
        break;
      }
      case 'combatEnd':
        if (e.won) sfx.playWin(sound);
        else sfx.playLose(sound);
        break;
      case 'resolve':
        trackResolveScore(run, e.report);
        break;
      default:
        break;
    }
  }
  lastLogCount = log.length;
}

function beginPlayback(displayOrigin: (GemKind | null)[], report: ResolveReport): void {
  playback = startPlayback(displayOrigin, report);
  emittedWave = -1;
}

function lastResolve(combat: Combat, from: number): ResolveReport | null {
  for (let i = combat.log.length - 1; i >= from; i--) {
    const e = combat.log[i];
    if (e && e.kind === 'resolve') return e.report;
  }
  return null;
}

/** Where a piece is about to land, computed on the pre-play board. */
interface DropHint {
  readonly piece: Piece;
  readonly column: number;
  readonly rotation: number;
}

/**
 * Snapshot, act, and start cascade playback if anything resolved.
 *
 * Playback begins from the board WITH the played piece already on it, not
 * from before it was placed. The piece does not fall in from the sky — it is
 * placed, and what animates is what the clear then leaves behind. Survivors
 * fall from their own cells, which is the read the player asked for.
 */
function act(combat: Combat, fn: () => void, drop?: DropHint): void {
  const snapshot = combat.grid.slice();
  const before = combat.log.length;

  // Work out where the piece will land, against the pre-play board.
  let placed: { cells: number[]; kinds: GemKind[] } | undefined;
  if (drop) {
    const rotated = rotatePiece(drop.piece, drop.rotation);
    const row = restRow(snapshot, rotated.cells, drop.column);
    if (row !== null) {
      placed = {
        cells: rotated.cells.map(([dr, dc]) => idx(row + dr, drop.column + dc)),
        kinds: [...rotated.kinds],
      };
    }
  }

  fn();
  const resolve = lastResolve(combat, before);
  if (!resolve) {
    drainCombatEvents(battleLayout(combat));
    return;
  }

  // Display origin: the pre-play board with the piece committed to it.
  const origin = snapshot.slice();
  if (placed) {
    for (let i = 0; i < placed.cells.length; i++) {
      const cell = placed.cells[i];
      const kind = placed.kinds[i];
      if (cell !== undefined && kind) origin[cell] = kind;
    }
  }
  beginPlayback(origin, resolve);
  drainCombatEvents(battleLayout(combat));
}

/**
 * Slay-the-Spire style card handling: press a card, drag it into the well,
 * release to play. Tap-to-select still works, but dragging is the primary
 * gesture because it cannot fire by accident — the card has to travel.
 */
const DRAG_THRESHOLD = 10;
const DOUBLE_TAP_MS = 340;
/** Card uid and timestamp of the last tap, for tap-tap-to-fire. */
let doubleTapUid = -1;
let doubleTapAt = 0;
interface DragState {
  readonly card: CardInstance;
  readonly index: number;
  readonly startX: number;
  readonly startY: number;
  active: boolean;
  x: number;
  y: number;
}
let drag: DragState | null = null;

/** Where the dragged card is drawn, or null when nothing is being dragged. */
function dragPosition(): { x: number; y: number; index: number } | null {
  return drag && drag.active ? { x: drag.x, y: drag.y, index: drag.index } : null;
}

// --- device orientation rotation ------------------------------------------
// On a phone the piece rotates with the phone: roll the device, the piece
// turns. On desktop there is no gyroscope, so R and the ROTATE button carry it.
// The button is present either way, because a gyroscope can be denied.

const coarsePointer =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;

let tiltEnabled = false;
let tiltArmed = 0;

/** Degrees of roll past which one rotation fires. Deliberately generous. */
const TILT_STEP = 26;
/** Degrees of near-level roll needed to re-arm after a rotation. */
const TILT_DEAD = 11;

function onDeviceOrientation(e: DeviceOrientationEvent): void {
  // Only a held piece rotates; tilting during anything else is the player
  // adjusting their grip, not a command.
  if (!isAiming(controller) || controller.aim.mode !== 'piece') {
    tiltArmed = 0;
    return;
  }
  const gamma = e.gamma ?? 0;
  if (Math.abs(gamma) < TILT_DEAD) {
    tiltArmed = 0;
    return;
  }
  if (tiltArmed !== 0) return;

  if (gamma > TILT_STEP) {
    rotateAim(controller);
    tiltArmed = -1;
  } else if (gamma < -TILT_STEP) {
    rotateAim(controller);
    tiltArmed = 1;
  }
}

/**
 * Ask for orientation access. iOS gates this behind a permission prompt that
 * must be raised from a user gesture, so this is called from the first tap
 * rather than at startup. Failure is not an error — the ROTATE button still
 * works, and the player simply never gets the gyroscope.
 */
function enableTilt(): void {
  if (tiltEnabled) return;
  const DOE = window.DeviceOrientationEvent as unknown as {
    requestPermission?: () => Promise<string>;
  } | undefined;
  if (!DOE) return;

  const attach = () => {
    window.addEventListener('deviceorientation', onDeviceOrientation);
    tiltEnabled = true;
  };

  if (typeof DOE.requestPermission === 'function') {
    DOE.requestPermission().then((r) => {
      if (r === 'granted') attach();
    }).catch(() => {
      // Denied or unsupported. The button is the fallback.
    });
  } else {
    attach();
  }
}

/** Commit a held aim at a screen position, honouring the piece-drop hint. */
function commitAimAt(combat: Combat, layout: Layout, x: number, y: number): void {
  if (!isAiming(controller)) return;
  sfx.playCard(sound);
  let drop: DropHint | undefined;
  const held = controller.aim.card?.def.piece;
  if (controller.aim.mode === 'piece' && held) {
    const column = columnAt(layout, x, y) ?? controller.aim.column;
    drop = { piece: held, column, rotation: controller.aim.rotation };
  }
  act(
    combat,
    () => {
      pointerDown(controller, combat, x, y);
    },
    drop,
  );
}

// --- input ----------------------------------------------------------------

function pointerPos(e: PointerEvent): { x: number; y: number } {
  const r = renderer.canvas.getBoundingClientRect();
  return { x: e.clientX - r.left, y: e.clientY - r.top };
}

function rewardHover(layout: Layout, x: number, y: number): number | null {
  for (let i = 0; i < layout.rewardCards.length; i++) {
    const r = layout.rewardCards[i];
    if (r && contains(r, x, y)) return i;
  }
  return contains(layout.skipButton, x, y) ? -1 : null;
}

function socketHover(layout: Layout, deck: readonly unknown[], x: number, y: number): number | null {
  const rects = socketCardRects(layout, deck.length);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r && contains(r, x, y)) return i;
  }
  return contains(layout.skipButton, x, y) ? -1 : null;
}

function restHover(layout: Layout, x: number, y: number): number | null {
  const rects = restOptionRects(layout);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r && contains(r, x, y)) return i;
  }
  return null;
}

function purgeHover(layout: Layout, count: number, x: number, y: number): number | null {
  const rects = purgeCardRects(layout, count);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r && contains(r, x, y)) return i;
  }
  return null;
}

/** -1 is the back button; null is empty space. */
function craftHover(layout: Layout, x: number, y: number): number | null {
  const rects = craftOptionRects(layout, CRAFT_RECIPES.length);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r && contains(r, x, y)) return i;
  }
  if (contains(craftBackRect(layout), x, y)) return -1;
  return null;
}

function cacheHover(layout: Layout, count: number, x: number, y: number): number | null {
  const rects = cacheOptionRects(layout, count);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r && contains(r, x, y)) return i;
  }
  return null;
}

function socketExpandHover(layout: Layout, deck: readonly unknown[], x: number, y: number): number | null {
  const rects = socketCardRects(layout, deck.length);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (r && contains(r, x, y)) return i;
  }
  return null;
}

renderer.canvas.addEventListener('pointermove', (e) => {
  const { x, y } = pointerPos(e);
  mouseX = x;
  mouseY = y;
  const combat = activeCombat();
  if (!combat) return;
  const layout = battleLayout(combat);
  updateHover(controller, layout, combat, x, y);

  if (!drag) return;
  drag.x = x;
  drag.y = y;
  if (drag.active) return;

  // The card only becomes a play once it has actually travelled. Until then a
  // release is a tap, and a tap selects rather than fires.
  const dx = x - drag.startX;
  const dy = y - drag.startY;
  if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;

  drag.active = true;
  sfx.playCard(sound);
  // Cards with no target play themselves; everything else enters aim so the
  // ghost can follow the finger.
  beginAim(controller, combat, drag.card);
  updateHover(controller, layout, combat, x, y);
});

renderer.canvas.addEventListener('pointerup', (e) => {
  if (!drag) return;
  const d = drag;
  drag = null;
  try {
    renderer.canvas.releasePointerCapture(e.pointerId);
  } catch {
    // Not every pointer was captured.
  }

  const combat = activeCombat();
  if (!combat || busy()) return;
  const layout = battleLayout(combat);
  const { x, y } = pointerPos(e);
  updateHover(controller, layout, combat, x, y);

  // Released without travelling: a tap.
  //
  // A card with no target plays itself immediately. One that needs aiming
  // selects on the first tap and plays on the second — so the scheme is
  // tap-to-raise, tap-again-to-fire, or drag-to-fire. All three paths converge
  // on the same commit, so there is only one place a card can actually leave
  // the hand.
  if (!d.active) {
    if (d.card.def.target === 'none') {
      clickCard(controller, combat, d.index);
      return;
    }

    const now = performance.now();
    if (doubleTapUid === d.card.uid && now - doubleTapAt < DOUBLE_TAP_MS) {
      doubleTapUid = -1;
      doubleTapAt = 0;
      // The first tap already raised it; make sure aim is live, then commit.
      if (!isAiming(controller)) clickCard(controller, combat, d.index);
      commitAimAt(combat, layout, x, y);
      return;
    }
    doubleTapUid = d.card.uid;
    doubleTapAt = now;
    clickCard(controller, combat, d.index);
    return;
  }

  // Dragged back over the hand: the player changed their mind.
  if (contains(layout.hand, x, y)) {
    cancelAim(controller);
    return;
  }

  if (controller.aim.mode === 'idle') {
    // A no-target card: the drag was the whole gesture.
    act(combat, () => {
      clickCard(controller, combat, d.index);
    });
    return;
  }

  commitAimAt(combat, layout, x, y);
});

renderer.canvas.addEventListener('pointercancel', () => {
  if (drag) {
    drag = null;
    cancelAim(controller);
  }
});

renderer.canvas.addEventListener('pointerdown', (e) => {
  sfx.unlock(sound);
  // iOS requires the orientation prompt to come from a user gesture.
  if (coarsePointer) enableTilt();
  const { x, y } = pointerPos(e);

  if (run.phase === 'title' || run.phase === 'victory' || run.phase === 'defeat') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    // The seed badge is clickable anywhere it appears — a seed you cannot copy
    // is not shareable.
    if (contains(seedBadgeRect(layout), x, y)) {
      copySeed();
      return;
    }
    if (run.phase === 'title') {
      // Start the seed on screen. With ?seed= present that is the run the
      // link asked for; without it, the one generated at load.
      newRun(false);
      return;
    }
    if (!busy()) newRun(true);
    return;
  }

  if (run.phase === 'reward') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const options = run.rewardOptions ?? [];
    if (options.length === 0) {
      if (contains(layout.skipButton, x, y)) {
        continueReward(run);
        lastLogCount = 0;
      }
      return;
    }
    const pick = rewardHover(layout, x, y);
    if (pick === null) return;
    if (pick === -1) skipReward(run);
    else chooseRewardOption(run, pick);
    lastLogCount = 0;
    return;
  }

  if (run.phase === 'cache') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const options = run.cacheOptions ?? [];
    const pick = cacheHover(layout, options.length, x, y);
    if (pick === null) return;
    chooseCacheReward(run, pick);
    lastLogCount = 0;
    return;
  }

  if (run.phase === 'socket_expand') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const pick = socketExpandHover(layout, run.deck, x, y);
    if (pick === null) return;
    expandSocket(run, pick);
    lastLogCount = 0;
    return;
  }

  if (run.phase === 'socket') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const pick = socketHover(layout, run.deck, x, y);
    if (pick === null) return;
    if (pick === -1) skipSocket(run);
    else socketEnchant(run, pick);
    lastLogCount = 0;
    return;
  }

  if (run.phase === 'rest') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const rects = restOptionRects(layout);
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (r && contains(r, x, y)) {
        const options = restOptions(run);
        const opt = options[i];
        if (opt?.available) {
          const before = run.hp;
          restAction(run, opt.action);
          if (run.hp > before) {
            mapNotice = `RESTED  +${run.hp - before} HP`;
            mapNoticeAt = performance.now();
          }
        }
        return;
      }
    }
    return;
  }

  if (run.phase === 'purge') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const rects = purgeCardRects(layout, run.deck.length);
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (r && contains(r, x, y)) {
        purgeCard(run, i);
        return;
      }
    }
    return;
  }

  if (run.phase === 'craft') {
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const pick = craftHover(layout, x, y);
    if (pick === -1) {
      run.phase = 'rest';
      return;
    }
    if (pick !== null) {
      const recipe = CRAFT_RECIPES[pick];
      if (recipe) craftGem(run, recipe.id);
    }
    return;
  }

  if (run.phase === 'map') {
    if (busy()) return;
    const size = layoutSize();
    const layout = computeLayout(size.width, size.height, 0);
    const node = mapNodeAt(layout, x, y);
    if (node) chooseMapNode(node.id);
    return;
  }

  const combat = activeCombat();
  if (!combat) return;
  const layout = battleLayout(combat);

  if (e.button === 2) {
    cancelAim(controller);
    return;
  }
  if (busy()) return;

  // Touch has no hover. Deriving aim state from the tap itself is what makes
  // a phone behave identically to a mouse rather than requiring a second tap
  // to move the ghost before the first one means anything.
  updateHover(controller, layout, combat, x, y);

  // Dedicated rotate and cancel controls. Aiming must never depend on hover,
  // a right-click, or a clever gesture — there is always a button.
  if (isAiming(controller) && contains(layout.rotate, x, y)) {
    rotateAim(controller);
    return;
  }
  if (isAiming(controller) && contains(layout.cancel, x, y)) {
    cancelAim(controller);
    return;
  }

  if (controller.hover.enemyIndex !== null && !isAiming(controller)) {
    setTarget(combat, controller.hover.enemyIndex);
    return;
  }

  if (controller.hover.overEndTurn && !isAiming(controller)) {
    if (combat.phase === 'play') endPlayerTurn(combat);
    return;
  }

  // Pressing a card starts a drag. Nothing is played until the card travels —
  // a stray tap on a card is inert, which is the whole reason for this
  // control scheme.
  //
  // This check runs BEFORE the held-aim commit below. Clicking a different
  // card while one is already raised must switch selection, not fire the
  // raised card at the new card's position — which is exactly what happened
  // when the commit branch came first.
  const handIndex = handIndexAt(layout, x, y);
  if (handIndex !== null && combat.phase === 'play') {
    const card = combat.hand[handIndex];
    if (card && canPlay(combat, card)) {
      drag = { card, index: handIndex, startX: x, startY: y, active: false, x, y };
      try {
        renderer.canvas.setPointerCapture(e.pointerId);
      } catch {
        // Pointer capture is an optimisation, not a requirement.
      }
      return;
    }
  }

  // A click that missed every card is a click in the play area: commit the
  // held aim there.
  if (isAiming(controller)) {
    commitAimAt(combat, layout, x, y);
    return;
  }

  // No card, no enemy, no End Turn: a click in the well starts the free swap.
  if (controller.hover.cell !== null && !combat.freeSwapUsed) {
    beginFreeSwap(controller, controller.hover.cell);
  }
});

renderer.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

window.addEventListener('keydown', (e) => {
  sfx.unlock(sound);

  // Debug harness. Simulated viewports so screen sizes can be exercised
  // without DevTools, and a bounds overlay for seeing what the layout thinks
  // each region is. Development affordances; harmless in a build.
  const vpKeys: Record<string, { width: number; height: number }> = {
    F1: VIEWPORTS.narrow,
    F2: VIEWPORTS.standard,
    F3: VIEWPORTS.tall,
    F4: VIEWPORTS.desktop,
  };
  const vp = vpKeys[e.key];
  if (vp) {
    forceViewport(vp);
    return;
  }
  if (e.key === 'F5') {
    forceViewport(null);
    return;
  }
  if (e.key === 'b' || e.key === 'B') {
    setDebugBounds(!debugBoundsOn());
    return;
  }

  // Match-threshold toggle for the balance experiment. Works on any screen.
  if (e.key === 'q' || e.key === 'Q') {
    setMatchMin(getMatchMin() === 3 ? 4 : 3);
    return;
  }

  // Board-size cycle: 7×9 → 7×10 → 8×10 → 7×9.
  if (e.key === 't' || e.key === 'T') {
    const current = BOARD_SIZES.findIndex(b => b.cols === WELL_COLS && b.rows === WELL_ROWS);
    const next = BOARD_SIZES[(current + 1) % BOARD_SIZES.length];
    if (next) setBoardSize(next.cols, next.rows);
    else resetBoardSize();
    return;
  }

  // Seed-density cycle: 0 → 2 → 3 → 4 → 0. Board population at fight start.
  if (e.key === 'd' || e.key === 'D') {
    const levels = [0, 2, 3, 4];
    const current = levels.indexOf(getSeedDensity());
    const next = levels[(current + 1) % levels.length];
    if (next !== undefined) setSeedDensity(next);
    return;
  }

  if (run.phase === 'title') {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      // Start the seed on screen. ?seed= makes this the run the link asked for.
      newRun(false);
    } else if (e.key === 'r' || e.key === 'R') {
      newRun(true);
    } else if (e.key === 'c' || e.key === 'C') {
      copySeed();
    }
    return;
  }

  if (run.phase === 'victory' || run.phase === 'defeat') {
    if (busy()) return;
    if (e.key === 'r' || e.key === 'R') {
      // The correctness requirement: same seed, same run, from a fresh start.
      restartSameSeed();
    } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      newRun(true);
    } else if (e.key === 'c' || e.key === 'C') {
      copySeed();
    }
    return;
  }

  if (run.phase === 'reward') {
    const limit = run.rewardOptions?.length ?? 0;
    if (limit === 0) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
        e.preventDefault();
        continueReward(run);
        lastLogCount = 0;
      }
      return;
    }
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= limit) {
      chooseRewardOption(run, n - 1);
      lastLogCount = 0;
    } else if (e.key === 'Escape') {
      skipReward(run);
      lastLogCount = 0;
    }
    return;
  }

  if (run.phase === 'cache') {
    const limit = run.cacheOptions?.length ?? 0;
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= limit) {
      chooseCacheReward(run, n - 1);
      lastLogCount = 0;
    } else if (e.key === 'Escape') {
      skipCache(run);
      lastLogCount = 0;
    }
    return;
  }

  if (run.phase === 'socket_expand') {
    if (e.key === 'Escape') {
      skipCache(run);
      lastLogCount = 0;
      return;
    }
    const n = e.key === '0' ? 10 : Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= run.deck.length) {
      expandSocket(run, n - 1);
      lastLogCount = 0;
    }
    return;
  }

  if (run.phase === 'socket') {
    if (e.key === 'Escape') {
      skipSocket(run);
      lastLogCount = 0;
      return;
    }
    const n = e.key === '0' ? 10 : Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= run.deck.length) {
      socketEnchant(run, n - 1);
      lastLogCount = 0;
    }
    return;
  }

  if (run.phase === 'rest') {
    const n = Number(e.key);
    const opts = restOptions(run);
    if (Number.isInteger(n) && n >= 1 && n <= opts.length) {
      const opt = opts[n - 1];
      if (opt?.available) {
        const before = run.hp;
        restAction(run, opt.action);
        if (run.hp > before) {
          mapNotice = `RESTED  +${run.hp - before} HP`;
          mapNoticeAt = performance.now();
        }
      }
    }
    return;
  }

  if (run.phase === 'purge') {
    if (e.key === 'Escape') {
      run.phase = 'rest';
      return;
    }
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= run.deck.length) {
      purgeCard(run, n - 1);
    }
    return;
  }

  if (run.phase === 'craft') {
    if (e.key === 'Escape') {
      run.phase = 'rest';
      return;
    }
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= CRAFT_RECIPES.length) {
      const recipe = CRAFT_RECIPES[n - 1];
      if (recipe) craftGem(run, recipe.id);
    }
    return;
  }

  if (run.phase === 'map') {
    const n = Number(e.key);
    const avail = availableNodes(run);
    if (Number.isInteger(n) && n >= 1 && n <= avail.length) {
      const node = avail[n - 1];
      if (node) chooseMapNode(node.id);
      mapFocusIndex = -1;
      return;
    }
    // Arrow keys cycle keyboard focus through visible nodes for tooltip display.
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      const ids = visibleMapNodeIds();
      if (ids.length > 0) mapFocusIndex = mapFocusIndex < 0 ? 0 : (mapFocusIndex + 1) % ids.length;
      e.preventDefault();
      return;
    }
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      const ids = visibleMapNodeIds();
      if (ids.length > 0) mapFocusIndex = mapFocusIndex < 0 ? ids.length - 1 : (mapFocusIndex - 1 + ids.length) % ids.length;
      e.preventDefault();
      return;
    }
    if (e.key === 'Escape') {
      mapFocusIndex = -1;
      return;
    }
    return;
  }

  const combat = activeCombat();
  if (!combat) return;

  if (e.key === 'Escape') {
    cancelAim(controller);
    return;
  }
  if (e.key === 'r' || e.key === 'R') {
    if (!busy()) rotateAim(controller);
    return;
  }
  if (e.key === 'm' || e.key === 'M') {
    sfx.setMuted(sound, !sound.muted);
    return;
  }
  if (e.key === 'e' || e.key === 'E' || e.key === ' ' || e.key === 'Enter') {
    if (combat.phase === 'play' && !isAiming(controller) && !busy()) {
      e.preventDefault();
      endPlayerTurn(combat);
    }
    return;
  }

  const n = Number(e.key);
  if (Number.isInteger(n) && n >= 1 && n <= 9 && !busy()) {
    sfx.playCard(sound);
    act(combat, () => {
      clickCard(controller, combat, n - 1);
    });
  }
});

// --- loop -----------------------------------------------------------------

let raf = 0;
let last = performance.now();

function frame(now: number): void {
  raf = requestAnimationFrame(frame);
  const dt = Math.min((now - last) / 1000, 0.05);
  // Reduced motion keeps every beat legible but runs them ~3x faster, so
  // nothing is hidden — it just arrives without the theatrics.
  // Reduced motion suppresses shake, flash and particles. It does NOT speed up
  // playback � a 3x speedup makes the core presentation harder to follow, not
  // gentler, and running the cascade faster directly contradicts slowing it
  // down three times at the player's request.
  const playbackDt = dt;
  last = now;

  const combat = activeCombat();

  // Transition out of a finished fight once the cascade has played and a beat
  // has passed, so the win/lose overlay is actually readable.
  if (run.phase === 'battle' && combat && (combat.phase === 'won' || combat.phase === 'lost')) {
    if (endTimer < 0) endTimer = 1.1;
    if (!busy()) {
      endTimer -= dt;
      if (endTimer <= 0) {
        settleBattle(run);
        lastLogCount = 0;
        endTimer = -1;
        // The next fight is a new board; old ghost tracks would slide its
        // first prediction in from cells that no longer exist.
        resetGhostTracks();
        resetAnims();
        handScroll = 0;
      }
    }
  } else if (run.phase === 'battle') {
    endTimer = -1;
  }

  // The hand tracks the WELL's centre, not the stage's. The well is what the
  // player is reading cards against, so if the two ever disagree the cards are
  // the ones that must move. With nothing focused, the middle card stays put
  // so the hand reads as centred on the board rather than parked at an edge.
  let layout = battleLayout(combat);
  if (combat && run.phase === 'battle' && combat.hand.length > 0) {
    const focus = centeredHandIndex(combat);
    const idx = focus >= 0 ? focus : Math.floor((combat.hand.length - 1) / 2);
    const card = layout.cards[idx];
    if (card) {
      const anchorX = layout.well.x + layout.well.w / 2;
      const target = handScroll + (card.x + card.w / 2) - anchorX;
      const maxScroll = Math.max(0, layout.handContentW - layout.hand.w);
      const clamped = Math.max(0, Math.min(target, maxScroll));
      const next = handScroll + (clamped - handScroll) * Math.min(1, dt * 12);
      if (Math.abs(next - handScroll) > 0.05) {
        handScroll = next;
        layout = battleLayout(combat);
      }
    }
  }

  if (playback) {
    if (playback.phase === 'flash' && playback.index !== emittedWave) {
      emittedWave = playback.index;
      emitWave(layout, playback);
    }
    tickPlayback(playback, playbackDt);
    if (playbackDone(playback)) playback = null;
  }

  if (turnPlayback) {
    // Emit the beat's fx the moment it becomes current, not every frame.
    if (turnPlayback.index !== turnStepEmitted) {
      turnStepEmitted = turnPlayback.index;
      turnLanded = false;
      emitTurnStep(layout, turnPlayback);
    }
    const raw = stepProgress(turnPlayback);
    const cur = currentStep(turnPlayback);
    // Landing is its own event, not just the end of the fall: squash, flash,
    // and a heavier thud for Stone than for a coloured piece.
    if (!turnLanded && cur && (cur.kind === 'pieceDrop' || cur.kind === 'stoneDrop') && raw >= 0.86) {
      turnLanded = true;
      const isStone = (cur.kinds ?? []).every(k => k === 'stone');
      addShake(fx, isStone ? 7 : 3.5);
      addFlash(fx, isStone ? 0.16 : 0.09);
      if (isStone) sfx.playStone(sound);
      else sfx.playLock(sound);
      if (cur.cells) {
        for (const cell of cur.cells) {
          const r = cellRect(layout, cell);
          emitBurst(fx, r.x + r.w / 2, r.y + r.h / 2, isStone ? PALETTE.inert : PALETTE.text, 5, 90);
        }
      }
    }
    tickTurnPlayback(turnPlayback, playbackDt);
    if (turnPlaybackDone(turnPlayback)) {
      turnPlayback = null;
      // Anything the beats did not cover — chiefly combatEnd — lands now.
      drainCombatEvents(layout);
    }
  }

  updateFx(fx, dt);
  advanceAnims();
  // The copy confirmation is a flash of feedback, not a permanent state.
  if (seedCopied && performance.now() - seedCopiedAt > 1800) seedCopied = false;

  renderer.beginFrame();
  const shake = shakeOffset(fx);
  ctx.save();
  ctx.translate(shake.x, shake.y);

  if (run.phase === 'title') {
    drawTitle(ctx, layout, seedCode(), seedCopied);
  } else if (run.phase === 'map') {
    const focusIds = visibleMapNodeIds();
    const focusId = mapFocusIndex >= 0 && mapFocusIndex < focusIds.length ? focusIds[mapFocusIndex] : null;
    drawMap(ctx, layout, run, {
      notice: activeNotice(),
      hoverId: visibleMapNodeAt(layout, mouseX, mouseY)?.id ?? null,
      focusId,
    });
  } else if (run.phase === 'reward') {
    const options = run.rewardOptions ?? [];
    if (options.length === 0) {
      drawComponentReward(ctx, layout, run.lastRewardDrops, run.components, contains(layout.skipButton, mouseX, mouseY));
    } else {
      drawRewardScreen(ctx, layout, options, run.lastRewardDrops, run.components, rewardHover(layout, mouseX, mouseY));
    }
  } else if (run.phase === 'cache') {
    const options = run.cacheOptions ?? [];
    drawCacheScreen(ctx, layout, run, cacheHover(layout, options.length, mouseX, mouseY));
  } else if (run.phase === 'socket_expand') {
    drawSocketExpandScreen(ctx, layout, run, socketExpandHover(layout, run.deck, mouseX, mouseY));
  } else if (run.phase === 'socket' && run.enchantToSocket) {
    drawSocketScreen(ctx, layout, run, run.enchantToSocket, socketHover(layout, run.deck, mouseX, mouseY));
  } else if (run.phase === 'rest') {
    drawRestScreen(ctx, layout, run, restHover(layout, mouseX, mouseY));
  } else if (run.phase === 'purge') {
    drawPurgeScreen(ctx, layout, run, purgeHover(layout, run.deck.length, mouseX, mouseY));
  } else if (run.phase === 'craft') {
    drawCraftScreen(ctx, layout, run, craftHover(layout, mouseX, mouseY));
  } else if (run.phase === 'victory' || run.phase === 'defeat') {
    drawEndScreen(ctx, layout, run.phase === 'victory', run.fightsWon, currentFight(run)?.name ?? 'the deep', seedCode(), seedCopied, RUN_VERSION, run.score, run.hp);
  } else if (combat) {
    let display: DisplayState | undefined;
    if (turnPlayback) {
      const s = currentStep(turnPlayback);
      if (s) {
        const raw = stepProgress(turnPlayback);
        const isDrop = s.kind === 'pieceDrop' || s.kind === 'stoneDrop';
        // The piece hangs above the lip first and its landing cells pulse, so
        // the eye is already where the action is before anything moves.
        const HOLD = 0.28;
        const held = isDrop && raw < HOLD;
        const fallK = isDrop ? (held ? 0 : easeInQuad((raw - HOLD) / (1 - HOLD))) : 0;
        let stepGrid = s.grid;
        let stepFlashing: readonly number[] = held ? (s.cells ?? []) : [];
        let stepFlashK = held ? 0.35 : 0;

        // Rebuild an enemy cascade from its landed-board snapshot, just like
        // player cascade playback. This is the missing visual evidence that
        // explains any damage, Block, or poison caused by the drop.
        if (s.kind === 'enemyResolve' && s.report && s.report.waves.length > 0) {
          const waves = s.report.waves;
          const scaled = Math.min(raw * waves.length, waves.length - Number.EPSILON);
          const waveIndex = Math.floor(scaled);
          const local = scaled - waveIndex;
          const reconstructed = s.grid.slice();
          for (let i = 0; i < waveIndex; i++) {
            const prior = waves[i];
            if (!prior) continue;
            clearCells(reconstructed, prior.cleared);
            applyGravity(reconstructed);
          }
          const wave = waves[waveIndex];
          if (wave && local < 0.58) {
            stepFlashing = wave.cleared;
            stepFlashK = Math.min(1, local / 0.58);
          } else if (wave) {
            clearCells(reconstructed, wave.cleared);
            applyGravity(reconstructed);
          }
          stepGrid = reconstructed;
        }

        display = {
          grid: stepGrid,
          flashing: stepFlashing,
          flashK: stepFlashK,
          falling: isDrop
            ? { cells: s.cells ?? [], kinds: s.kinds ?? [], k: fallK }
            : undefined,
          // The whole frozen moment. Without this the panel would already read
          // next turn's intent while this turn's attack is still landing.
          view: s.view,
          actingEnemy: s.enemyUid > 0 ? s.enemyUid : undefined,
        };
      }
    } else if (playback) {
      // The piece is already on the board at frame one. What animates is what
      // the clear left behind — each survivor falling from its own cell.
      const k = fallProgress(playback);
      const falls = playback.falls;
      display = {
        grid: playback.display,
        flashing: flashingCells(playback),
        flashK: flashProgress(playback),
        falling:
          falls.length > 0
            ? {
                cells: falls.map(f => f.cell),
                kinds: falls.map(f => f.kind),
                fromRows: falls.map(f => f.fromRow),
                k,
              }
            : undefined,
      };
    }
    drawGame(ctx, layout, combat, controller, display, {
      enemy: enemyAnims,
      player: playerAnim,
    }, dragPosition(), tiltEnabled);
    const fight = currentFight(run);
    if (fight) drawFloorBanner(ctx, layout, fight.name, runProgress(run));
  }

  drawFx(ctx, fx, renderer.width, renderer.height);
  ctx.restore();
  renderer.endFrame();
}

raf = requestAnimationFrame(frame);

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cancelAnimationFrame(raf);
    renderer.destroy();
  });
}
